import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, IconButton, Typography, Card, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const GENERATION_COLORS = [
  '#9b59b6', '#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#1abc9c', '#e67e22', '#95a5a6'
];

const FanChartView = ({ data, selectedPerson, onPersonClick, onBack, isMobile }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredPerson, setHoveredPerson] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const chartRef = useRef({ root: null, radius: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!data || data.length === 0 || dimensions.width === 0) return;

    console.log('Building sunburst with', data.length, 'people');

    // Build tree
    const personMap = new Map();
    data.forEach(p => {
      personMap.set(p._id, {
        name: p.name,
        id: p._id,
        gender: p.gender,
        status: p.status,
        role: p.role,
        children: []
      });
    });

    data.forEach(p => {
      if (p.children && Array.isArray(p.children)) {
        const parent = personMap.get(p._id);
        p.children.forEach(childId => {
          const cid = typeof childId === 'object' ? childId._id : childId;
          const child = personMap.get(cid);
          if (child && parent) {
            parent.children.push(child);
          }
        });
      }
    });

    // Find root
    let root = null;
    let maxChildren = 0;
    personMap.forEach(person => {
      if (person.children.length > maxChildren) {
        maxChildren = person.children.length;
        root = person;
      }
    });

    if (!root) {
      root = personMap.values().next().value;
    }

    console.log('Root:', root.name, 'with', root.children.length, 'children');

    // Setup SVG
    const width = dimensions.width;
    const height = dimensions.height;
    const radius = Math.min(width, height) / 2;
    chartRef.current.radius = radius;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', [-width / 2, -height / 2, width, height])
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');

    // Create hierarchy
    const hierarchy = d3.hierarchy(root, d => d.children)
      .sum(() => 1)
      .sort((a, b) => b.value - a.value);

    console.log('Hierarchy nodes:', hierarchy.descendants().length);
    chartRef.current.root = hierarchy;

    // Create partition
    const partition = data => {
      const root = d3.hierarchy(data)
        .sum(() => 1)
        .sort((a, b) => b.value - a.value);
      return d3.partition()
        .size([2 * Math.PI, root.height + 1])(root);
    };

    const partitioned = partition(root);

    // Arc generator
    const arc = d3.arc()
      .startAngle(d => d.x0)
      .endAngle(d => d.x1)
      .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius * 1.5)
      .innerRadius(d => d.y0 * radius)
      .outerRadius(d => Math.max(d.y0 * radius, d.y1 * radius - 1));

    // Draw paths
    const path = g.append('g')
      .selectAll('path')
      .data(partitioned.descendants().slice(1))
      .join('path')
      .attr('fill', d => {
        const color = GENERATION_COLORS[d.depth % GENERATION_COLORS.length];
        return d.data.gender === 'FEMALE' ? d3.color(color).brighter(0.3) : color;
      })
      .attr('fill-opacity', d => 0.6 + (d.data.children && d.data.children.length > 0 ? 0 : 0.4))
      .attr('pointer-events', 'all')
      .attr('d', arc)
      .style('cursor', 'pointer')
      .on('mouseenter', function(event, d) {
        d3.select(this).attr('fill-opacity', 1);
        setHoveredPerson(d.data);
      })
      .on('mouseleave', function(event, d) {
        d3.select(this).attr('fill-opacity', 0.6 + (d.data.children && d.data.children.length > 0 ? 0 : 0.4));
        setHoveredPerson(null);
      })
      .on('click', (event, d) => {
        if (onPersonClick) onPersonClick(d.data);
      });

    path.append('title')
      .text(d => `${d.data.name}\n${d.value} descendants`);

    // Add labels with dynamic visibility based on zoom
    const labels = g.append('g')
      .attr('class', 'labels')
      .attr('pointer-events', 'none')
      .attr('text-anchor', 'middle')
      .style('user-select', 'none');

    const updateLabels = (transform) => {
      const k = transform.k;
      labels.selectAll('*').remove();

      partitioned.descendants().slice(1).forEach(d => {
        const arcAngle = d.x1 - d.x0;
        const arcRadius = (d.y0 + d.y1) / 2 * radius;
        const arcLength = arcAngle * arcRadius * k;

        // Show label if arc is long enough
        if (arcLength > 30 && d.y1 <= 4) {
          const angle = (d.x0 + d.x1) / 2;
          const x = Math.sin(angle) * arcRadius;
          const y = -Math.cos(angle) * arcRadius;
          
          let rotation = (angle * 180 / Math.PI - 90);
          if (rotation > 90) rotation += 180;

          const text = labels.append('text')
            .attr('transform', `translate(${x},${y}) rotate(${rotation})`)
            .attr('dy', '0.35em')
            .style('font-size', `${Math.max(9, Math.min(14, arcLength / 6))}px`)
            .style('font-weight', '600')
            .style('fill', '#fff')
            .style('text-shadow', '0 1px 3px rgba(0,0,0,0.9)');

          const name = d.data.name || 'Unknown';
          const maxChars = Math.floor(arcLength / 5);
          const displayName = name.length > maxChars ? name.substring(0, maxChars - 1) + '…' : name;
          text.text(displayName);
        }
      });
    };

    // Initial labels
    updateLabels(d3.zoomIdentity);

    // Add center circle for interaction
    g.append('circle')
      .attr('r', radius * 0.1)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('click', () => {
        if (onPersonClick) onPersonClick(root);
      });

    // Add center label for root
    const centerText = g.append('g')
      .attr('pointer-events', 'none')
      .attr('text-anchor', 'middle');

    centerText.append('text')
      .attr('y', '-10')
      .style('font-size', '20px')
      .style('font-weight', 'bold')
      .style('fill', '#fff')
      .style('text-shadow', '0 2px 4px rgba(0,0,0,0.8)')
      .text(root.name);

    centerText.append('text')
      .attr('y', '15')
      .style('font-size', '14px')
      .style('fill', 'rgba(255,255,255,0.7)')
      .text(`${root.children.length} children`);

    // Calculate appropriate zoom extent based on chart size
    const maxDepth = partitioned.height;
    const chartOuterRadius = (maxDepth + 1) * radius;
    const minScale = Math.min(
      width / (chartOuterRadius * 2.2),
      height / (chartOuterRadius * 2.2),
      0.1  // Minimum zoom level (10% of original size)
    );
    
    // Zoom with label updates
    const zoom = d3.zoom()
      .scaleExtent([minScale, 5])  // Allow more zoom out
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
        updateLabels(event.transform);
      });

    // Store current state for controls
    const currentState = {
      svg,
      zoom,
      g,
      width,
      height,
      radius,
      maxDepth,
      minScale
    };

    // Zoom controls
    window.handleZoomIn = () => {
      svg.transition().duration(300).call(zoom.scaleBy, 1.3);
    };

    window.handleZoomOut = () => {
      svg.transition().duration(300).call(zoom.scaleBy, 0.7);
    };

    window.handleReset = () => {
      svg.transition().duration(500).call(zoom.transform, d3.zoomIdentity);
    };

    window.handleFitScreen = () => {
      const { width, height, maxDepth, radius } = currentState;
      const chartOuterRadius = (maxDepth + 1) * radius;
      const targetScale = Math.min(
        width / (chartOuterRadius * 2.1),
        height / (chartOuterRadius * 2.1),
        1
      );
      
      const targetTransform = d3.zoomIdentity
        .translate(width / 2, height / 2)
        .scale(targetScale)
        .translate(-width / 2, -height / 2);
      
      svg.transition()
        .duration(750)
        .call(zoom.transform, targetTransform);
    };

    // Apply zoom behavior
    svg.call(zoom);

    // Start with fit screen view
    setTimeout(() => {
      window.handleFitScreen();
    }, 100);

    console.log('✅ Sunburst rendered with minScale:', minScale);

    return () => {
      delete window.handleZoomIn;
      delete window.handleZoomOut;
      delete window.handleReset;
      delete window.handleFitScreen;
    };

  }, [data, dimensions, selectedPerson, onPersonClick]);

  return (
    <Box style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)', zIndex: 1000 }}>
      {/* Header */}
      <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '15px 20px', background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', zIndex: 100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" style={{ color: '#fff', fontWeight: '700' }}>🎯 Family Sunburst</Typography>
        <IconButton onClick={onBack} style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      {/* SVG */}
      <Box ref={containerRef} style={{ width: '100%', height: '100%', paddingTop: '60px' }}>
        <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
      </Box>

      {/* Hover Info */}
      {hoveredPerson && (
        <Card style={{ position: 'absolute', top: '70px', right: '20px', background: 'rgba(0, 0, 0, 0.9)', border: '2px solid #9b59b6', borderRadius: '12px', padding: '12px', minWidth: '200px' }}>
          <Typography variant="h6" style={{ color: '#fff', marginBottom: '6px' }}>
            {hoveredPerson.gender === 'MALE' ? '♂' : hoveredPerson.gender === 'FEMALE' ? '♀' : '○'} {hoveredPerson.name}
          </Typography>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
            {hoveredPerson.gender} • {hoveredPerson.status}
          </Typography>
        </Card>
      )}

      {/* Controls */}
      <Card style={{ position: 'absolute', bottom: '20px', left: '20px', background: 'rgba(0, 0, 0, 0.85)', borderRadius: '12px', padding: '15px' }}>
        <Typography variant="subtitle2" style={{ color: '#fff', marginBottom: '10px', fontWeight: '600' }}>🎮 Controls</Typography>
        <Box display="flex" gap="8px" flexWrap="wrap">
          <Tooltip title="Zoom In">
            <IconButton onClick={() => window.handleZoomIn?.()} style={{ background: 'rgba(79, 172, 254, 0.2)', color: '#4facfe' }} size="small"><ZoomInIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton onClick={() => window.handleZoomOut?.()} style={{ background: 'rgba(79, 172, 254, 0.2)', color: '#4facfe' }} size="small"><ZoomOutIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Fit to Screen">
            <IconButton onClick={() => window.handleFitScreen?.()} style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }} size="small">
              <Box style={{ fontSize: '18px', fontWeight: 'bold' }}>⊡</Box>
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset View">
            <IconButton onClick={() => window.handleReset?.()} style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }} size="small"><RestartAltIcon /></IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.5)', display: 'block', marginTop: '8px', fontSize: '10px' }}>
          🖱️ Drag to pan • 🔍 Scroll to zoom
        </Typography>
      </Card>

      {/* Legend */}
      <Card style={{ position: 'absolute', bottom: '20px', right: '20px', background: 'rgba(0, 0, 0, 0.85)', borderRadius: '12px', padding: '12px', minWidth: '150px' }}>
        <Typography variant="subtitle2" style={{ color: '#fff', marginBottom: '8px', fontWeight: '600' }}>🎨 Generations</Typography>
        {[0, 1, 2, 3, 4].map(gen => (
          <Box key={gen} display="flex" alignItems="center" gap="6px" marginBottom="3px">
            <Box style={{ width: '12px', height: '12px', borderRadius: '2px', background: GENERATION_COLORS[gen] }} />
            <Typography variant="caption" style={{ color: '#fff', fontSize: '11px' }}>Gen {gen}</Typography>
          </Box>
        ))}
      </Card>

      <Typography variant="body2" style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
        Zoom: {(zoomLevel * 100).toFixed(0)}% • Hover segments for details
      </Typography>
    </Box>
  );
};

export default FanChartView;